import { useLocation, Link } from "wouter";
import { useAuth } from "@clerk/react";
import {
  Shield,
  LogOut,
  Activity,
  Stethoscope,
  Pill,
  BookOpen,
  Layers,
  FileText,
  Link2,
  Building2,
  Truck,
  Megaphone,
  ChevronRight,
  UserCheck,
  CalendarDays,
  AlertTriangle,
  Users,
  Network,
  Database,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface NavItem {
  href: string;
  label: string;
  icon: React.ElementType;
}

const navItems: NavItem[] = [
  { href: "/admin", label: "Security", icon: Shield },
  { href: "/admin/users", label: "Users", icon: Users },
  { href: "/admin/hcp-verifications", label: "HCP Verifications", icon: UserCheck },
  { href: "/admin/nhs-organisations", label: "NHS Organisations", icon: Network },
  { href: "/admin/conditions", label: "Conditions", icon: Stethoscope },
  { href: "/admin/medications", label: "Medications", icon: Pill },
  { href: "/admin/guidelines", label: "Guidelines", icon: BookOpen },
  { href: "/admin/scenarios", label: "Scenarios", icon: Layers },
  { href: "/admin/modules", label: "Learning Modules", icon: Activity },
  { href: "/admin/resources", label: "Resources", icon: Link2 },
  { href: "/admin/hospitals", label: "Hospitals & Trusts", icon: Building2 },
  { href: "/admin/supply-companies", label: "Supply Companies", icon: Truck },
  { href: "/admin/promotions", label: "Promotions", icon: Megaphone },
  { href: "/admin/events", label: "Events", icon: CalendarDays },
  { href: "/admin/safety-reports", label: "Safety Reports", icon: AlertTriangle },
  { href: "/admin/settings/retention", label: "Data Retention", icon: Database },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const { signOut } = useAuth();
  const [location] = useLocation();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-teal-700" />
          <span className="text-base font-bold text-slate-900">RheumHub Admin</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            clearRheumHubLocalStorage();
            await signOut();
            window.location.href = `${basePath}/`;
          }}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </Button>
      </header>

      <div className="flex flex-1 min-h-0">
        <nav className="w-56 bg-white border-r border-slate-200 flex-shrink-0 py-4 overflow-y-auto">
          {navItems.map((item) => {
            const isActive =
              item.href === "/admin"
                ? location === "/admin"
                : location.startsWith(item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-teal-50 text-teal-800 border-r-2 border-teal-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                )}
              >
                <item.icon className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1">{item.label}</span>
                {isActive && <ChevronRight className="h-3 w-3" />}
              </Link>
            );
          })}
        </nav>

        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
}

export function AdminPageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export function AdminTable({
  columns,
  rows,
  emptyMessage = "No entries yet.",
}: {
  columns: string[];
  rows: React.ReactNode[][];
  emptyMessage?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {columns.map((col) => (
                <th key={col} className="px-4 py-3 text-left font-medium text-slate-600 whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-slate-400 text-sm">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((cells, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors">
                  {cells.map((cell, j) => (
                    <td key={j} className="px-4 py-3 text-slate-700">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
